/**
 * MinIOConnector — S3-compatible object storage via MinIO.
 *
 * Auth: access-key + secret-key resolved via SecretProviderChain.
 * Peer dependency: minio (lazy-loaded).
 */

import type {
  ConnectContext,
  ConnectorContent,
  ConnectorDeclaration,
  ConnectorEntry,
  ConnectorFieldSpec,
  ConnectorHandle,
  OperationDescriptor,
  RuntimeSkillDescriptor,
  ToolFace,
} from "@skaile/workspaces/connectors";
import { AbstractConnector } from "@skaile/workspaces/connectors";

interface MinIOState {
  client: any; // minio.Client
  bucket: string;
  endpoint: string;
}

/**
 * Connector for MinIO and S3-compatible object storage.
 * Provides list, get, put, delete, and presign operations against a single bucket. Requires the `minio` optional peer dependency.
 * @docLink packages/factory-assets/concepts#minio-connector
 */
export class MinIOConnector extends AbstractConnector {
  readonly name = "minio";

  tools: ToolFace;

  constructor() {
    super();
    this.tools = {
      list: (handle, path) => this._list(handle, path),
      read: (handle, path) => this._read(handle, path),
      describeOperations: (handle) => this._describeOperations(handle),
      executeOp: (handle, operation, args) => this._executeOp(handle, operation, args),
      describeSkill: (handle, declaration) => this._describeSkill(handle, declaration),
    };
  }

  describeFields(): ConnectorFieldSpec[] {
    return [
      {
        key: "endpoint",
        label: "MinIO Endpoint",
        type: "url",
        required: true,
        sensitive: false,
        group: "Connection",
      },
      {
        key: "bucket",
        label: "Bucket",
        type: "text",
        required: true,
        sensitive: false,
        group: "Connection",
      },
      {
        key: "region",
        label: "Region",
        type: "text",
        required: false,
        sensitive: false,
        group: "Connection",
        default: "us-east-1",
      },
      {
        key: "use-ssl",
        label: "Use SSL",
        type: "boolean",
        required: false,
        sensitive: false,
        group: "Connection",
        default: "true",
      },
      {
        key: "access-key",
        label: "Access Key",
        type: "api-key",
        required: true,
        sensitive: true,
        group: "Authentication",
      },
      {
        key: "secret-key",
        label: "Secret Key",
        type: "password",
        required: true,
        sensitive: true,
        group: "Authentication",
      },
    ];
  }

  private _describeSkill(
    _handle: ConnectorHandle,
    declaration: ConnectorDeclaration,
  ): RuntimeSkillDescriptor {
    const endpoint = String(declaration.options?.endpoint ?? "minio");
    const bucket = String(declaration.options?.bucket ?? "unknown");
    return {
      id: declaration.id,
      name: `MinIO — ${bucket}`,
      description: `Object storage at ${endpoint}, bucket: ${bucket}`,
      category: "storage",
      access: declaration.access,
      toolNames: ["list_objects", "get_object", "put_object", "delete_object", "presign"],
    };
  }

  async connect(declaration: ConnectorDeclaration, ctx: ConnectContext): Promise<ConnectorHandle> {
    const endpoint = ctx.resolvedFields?.endpoint ?? String(declaration.options?.endpoint ?? "");
    const bucket = ctx.resolvedFields?.bucket ?? String(declaration.options?.bucket ?? "");
    const accessKey =
      ctx.resolvedFields?.["access-key"] ??
      ctx.secrets?.resolve((declaration.options?.["access-key"] as string | undefined) ?? "") ??
      "";
    const secretKey =
      ctx.resolvedFields?.["secret-key"] ??
      ctx.secrets?.resolve((declaration.options?.["secret-key"] as string | undefined) ?? "") ??
      "";
    const useSSL =
      (ctx.resolvedFields?.["use-ssl"] ?? declaration.options?.["use-ssl"] ?? "true") !== "false";

    if (!endpoint) throw new Error("MinIOAdapter requires an endpoint");
    if (!bucket) throw new Error("MinIOAdapter requires a bucket");
    if (!accessKey || !secretKey)
      throw new Error("MinIOAdapter requires access-key and secret-key");

    this.log.info("connect start", { endpoint, bucket });
    try {
      const minioMod = "minio";
      const { Client } = (await import(/* @vite-ignore */ minioMod)) as any;
      const url = new URL(endpoint);
      const client = new Client({
        endPoint: url.hostname,
        port: url.port ? Number(url.port) : useSSL ? 443 : 80,
        useSSL,
        accessKey,
        secretKey,
      });

      await client.bucketExists(bucket);
      this.log.info("connect ok", { endpoint, bucket });

      return this.makeHandle(declaration, { client, bucket, endpoint } as MinIOState);
    } catch (err) {
      this.log.error("connect failed", err, { endpoint, bucket });
      throw err;
    }
  }

  async disconnect(handle: ConnectorHandle): Promise<void> {
    this.log.info("disconnect");
    handle.status = "disconnected";
  }

  private _describeOperations(handle: ConnectorHandle): OperationDescriptor[] {
    const ops: OperationDescriptor[] = [
      {
        name: "list_objects",
        description: "List objects in the bucket",
        accessLevel: "read",
        args: [
          {
            name: "prefix",
            type: "string",
            required: false,
            description: "Object key prefix filter",
          },
          {
            name: "recursive",
            type: "boolean",
            required: false,
            description: "List recursively (default: false)",
          },
        ],
      },
      {
        name: "get_object",
        description: "Download an object and return its content",
        accessLevel: "read",
        args: [{ name: "key", type: "string", required: true, description: "Object key" }],
      },
      {
        name: "presign",
        description: "Generate a pre-signed URL for temporary access",
        accessLevel: "read",
        args: [
          { name: "key", type: "string", required: true, description: "Object key" },
          {
            name: "expiry",
            type: "number",
            required: false,
            description: "Expiry in seconds (default: 3600)",
          },
        ],
      },
    ];

    if (handle.access === "read-write") {
      ops.push(
        {
          name: "put_object",
          description: "Upload content as an object",
          accessLevel: "write",
          args: [
            { name: "key", type: "string", required: true, description: "Object key" },
            { name: "content", type: "string", required: true, description: "Object content" },
            {
              name: "content-type",
              type: "string",
              required: false,
              description: "MIME type (default: text/plain)",
            },
          ],
        },
        {
          name: "delete_object",
          description: "Delete an object from the bucket",
          accessLevel: "write",
          args: [{ name: "key", type: "string", required: true, description: "Object key" }],
        },
      );
    }

    return ops;
  }

  private async _list(
    handle: ConnectorHandle,
    path?: string,
    recursive = false,
  ): Promise<ConnectorEntry[]> {
    const { client, bucket } = handle.state as MinIOState;
    const entries: ConnectorEntry[] = [];
    const stream = client.listObjects(bucket, path ?? "", recursive);
    return new Promise((resolve, reject) => {
      stream.on("data", (obj: any) => {
        entries.push({
          name: obj.name,
          path: obj.name,
          type: "file",
          size: obj.size,
          modifiedAt: obj.lastModified,
        });
      });
      stream.on("end", () => resolve(entries));
      stream.on("error", reject);
    });
  }

  private async _read(handle: ConnectorHandle, path: string): Promise<ConnectorContent | null> {
    const { client, bucket } = handle.state as MinIOState;
    const chunks: Buffer[] = [];
    const stream = await client.getObject(bucket, path);
    return new Promise((resolve, reject) => {
      stream.on("data", (chunk: Buffer) => chunks.push(chunk));
      stream.on("end", () => resolve({ data: Buffer.concat(chunks).toString() }));
      stream.on("error", reject);
    });
  }

  private async _executeOp(
    handle: ConnectorHandle,
    operation: string,
    args: Record<string, unknown>,
  ): Promise<string> {
    const { client, bucket } = handle.state as MinIOState;

    switch (operation) {
      case "list_objects":
        return JSON.stringify(
          await this._list(handle, String(args.prefix ?? ""), Boolean(args.recursive)),
          null,
          2,
        );
      case "get_object": {
        const content = await this._read(handle, String(args.key));
        return content?.data?.toString() ?? "(empty)";
      }
      case "put_object": {
        const content = String(args.content ?? "");
        const contentType = String(args["content-type"] ?? "text/plain");
        const buf = Buffer.from(content);
        await client.putObject(bucket, String(args.key), buf, buf.length, {
          "Content-Type": contentType,
        });
        return "Object uploaded.";
      }
      case "delete_object": {
        await client.removeObject(bucket, String(args.key));
        return "Object deleted.";
      }
      case "presign":
        return await client.presignedGetObject(
          bucket,
          String(args.key),
          Number(args.expiry ?? 3600),
        );
      default:
        throw new Error(`MinIOAdapter has no operation "${operation}"`);
    }
  }
}

/**
 * Creates a new MinIOConnector instance.
 * @returns Configured MinIOConnector ready to be registered in the connector registry.
 * @docLink packages/factory-assets/api-reference#minio-connector-factory
 */
export function createConnector(): MinIOConnector {
  return new MinIOConnector();
}
